import {
  Grid,
  IconButton,
  Typography,
  FormGroup,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
} from '@mui/material';
import { FC, useState, useMemo } from 'react';
import { Comment, CommentOutlined } from '@mui/icons-material';
import {
  useStaffAttendanceActions,
  useStaffAttendanceState,
} from '../../contexts/staffAttendance.context';
import {
  ATTENDANCE_STATUS,
  TAttendanceByDateRecord,
  TAttendanceByDateResponse,
} from '../../../../../apis/__shared/attendance.interfaces';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import { useTranslation } from 'src/hooks/useTranslationHook';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import { useResponsive } from 'src/hooks/useResponsiveHook';

type StaffAttendanceTrackerProps = {
  isOpen: boolean;
  onCloseAttendanceTrackerModal: () => void;
};

const StaffAttendanceTracker: FC<StaffAttendanceTrackerProps> = (props) => {
  const { isOpen, onCloseAttendanceTrackerModal } = props;
  const { staffAttendanceRecords } = useStaffAttendanceState();
  const { updateAttendanceTrackerPayload } = useStaffAttendanceActions();
  const [trackedStaff, setTrackedStaff] = useState<{
    [staffId: string]: TAttendanceByDateRecord;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString()
  );
  const [openCommentFields, setOpenCommentFields] = useState<{
    [staffId: string]: boolean;
  }>({});
  const [comments, setComments] = useState<{
    [staffId: string]: string;
  }>({});

  const { translateText } = useTranslation();
  const { isMobile } = useResponsive();

  // Extract unique staff from attendance records
  const staffMap = useMemo(() => {
    const map = new Map<string, TAttendanceByDateRecord>();
    // Normalize to array-based structure: Array<{ date: string; records: TAttendanceRecord[] }>
    // Supports both array format (new) and object format (legacy)
    const recordsArray: Array<{
      date: string;
      records: TAttendanceByDateRecord[];
    }> = Array.isArray(staffAttendanceRecords)
        ? staffAttendanceRecords
        : Object.entries(staffAttendanceRecords).map(([date, records]) => ({
          date,
          records,
        }));

    recordsArray.forEach(({ records }) => {
      records.forEach((record: TAttendanceByDateRecord) => {
        const userId = record.userDetails.id;
        if (userId && !map.has(userId)) {
          map.set(userId, record);
        }
      });
    });
    return map;
  }, [staffAttendanceRecords]);

  const attendanceOptions = [
    {
      label: translateText('staff.attendance.present'),
      value: ATTENDANCE_STATUS.PRESENT,
      color: 'success',
    },
    {
      label: translateText('staff.attendance.absent'),
      value: ATTENDANCE_STATUS.ABSENT,
      color: 'error',
    },
    {
      label: translateText('staff.attendance.skipped'),
      value: ATTENDANCE_STATUS.SKIPPED,
      color: 'secondary',
    },
  ];

  const handleStaffTrackChange = (
    attendanceStatus: ATTENDANCE_STATUS,
    staffId: string
  ) => {
    const staffRecord = staffMap.get(staffId);
    if (!staffRecord) return;

    setTrackedStaff({
      ...trackedStaff,
      [staffId]: {
        attendanceId: new Date().getTime().toString(),
        userDetails: staffRecord.userDetails,
        attendanceStatus,
        comment: comments[staffId] || trackedStaff[staffId]?.comment,
        actions: staffRecord.actions || { edit: true, delete: true },
      },
    });
  };

  const handleCommentToggle = (staffId: string) => {
    setOpenCommentFields({
      ...openCommentFields,
      [staffId]: !openCommentFields[staffId],
    });
  };

  const handleCommentChange = (staffId: string, comment: string) => {
    setComments({
      ...comments,
      [staffId]: comment,
    });
    if (trackedStaff[staffId]) {
      setTrackedStaff({
        ...trackedStaff,
        [staffId]: {
          ...trackedStaff[staffId],
          comment,
        },
      });
    }
  };

  const onSaveAttendanceClickHandler = () => {
    if (Object.keys(trackedStaff).length < staffMap.size) {
      setErrorMessage(translateText('staff.attendance.selectAllStaff'));
      return;
    }
    setErrorMessage('');
    const attendanceRecords = Object.values(trackedStaff).map((record) => ({
      ...record,
      comment: comments[record.userDetails.id || ''] || record.comment,
    }));

    // Save in array format to match the normalized structure
    const payload: TAttendanceByDateResponse = [
      {
        date: selectedDate,
        records: attendanceRecords,
      },
    ];

    updateAttendanceTrackerPayload(payload);
    onCloseAttendanceTrackerModal();
  };

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onCloseAttendanceTrackerModal}
      title={translateText('staff.attendance.attendanceTracker')}
      footerPositiveAction={onSaveAttendanceClickHandler}
      footerPositiveActionText={translateText(
        'staff.attendance.saveAttendance'
      )}
      footerMessage={
        errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : (
          ''
        )
      }
    >
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          mx: 'auto',
          height: 'calc(100% - var(--tab-group-height) * 2)',
          py: { xs: 1, sm: 2 },
        }}
        id="attendanceTrackerTable"
        data-testid="staff-attendance-tracker"
      >
        <Grid
          item
          xs={12}
          sx={{
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Grid item xs={12} data-testid="attendance-tracker-date-label">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {translateText('staff.attendance.selectDate')}
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <ControlledDateField
              name="selectedDate"
              id="selectedDate"
              label={translateText('staff.attendance.date')}
              value={selectedDate}
              onChange={(date) => setSelectedDate(date.toISOString())}
            />
          </Grid>

          <Grid
            container
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              minHeight: { xs: 'auto', sm: 'var(--tab-group-height)' },
              px: { xs: 0, sm: 1 },
              mt: { xs: 1, sm: 0 },
              backgroundColor: 'var(--border_grey_light)',
            }}
            data-testid="attendance-tracker-header-desktop"
          >
            <Grid item xs={5}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                {translateText('staff.attendance.staff')}
              </Typography>
            </Grid>
            {attendanceOptions.map((eachAttendanceOption) => (
              <Grid item xs={2} key={eachAttendanceOption.value}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {eachAttendanceOption.label}
                </Typography>
              </Grid>
            ))}
            <Grid
              item
              xs={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CommentOutlined sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: `calc(100% - var(--tab-group-height))`,
            px: { xs: 0, sm: 1 },
            overflow: 'auto',
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 0 }} data-testid="attendance-tracker-table-body">
            {Array.from(staffMap.entries()).map(
              ([staffId, staffRecord], index: number) => {
                return (
                  <Grid
                    item
                    xs={12}
                    key={staffId}
                    sx={{
                      py: { xs: 1.5, sm: 1 },
                      borderTop:
                        index === 0
                          ? '1px solid var(--border_grey_light)'
                          : 'none',
                      borderBottom: '1px solid var(--border_grey_light)',
                      minHeight: { xs: 'auto', sm: 'var(--tab-group-height)' },
                    }}
                    data-testid="attendance-tracker-table-row"
                  >
                    <FormGroup
                      sx={{
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1, sm: 0 },
                      }}
                    >
                      <Grid container spacing={{ xs: 1, sm: 0 }}>
                        <Grid
                          item
                          xs={12}
                          sm={5}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 0.5, sm: 0 },
                          }}
                        >
                          <FormLabel
                            id={staffId}
                            sx={{
                              flex: 1,
                              fontSize: { xs: '0.875rem', sm: '0.875rem' },
                              fontWeight: { xs: 600, sm: 400 },
                            }}
                          >
                            {staffRecord.userDetails.firstName +
                              ' ' +
                              staffRecord.userDetails.lastName}
                          </FormLabel>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            display: { xs: 'flex', sm: 'block' },
                            flexDirection: { xs: 'row', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'center' },
                            gap: { xs: 1, sm: 0 },
                          }}
                        >
                          <RadioGroup
                            aria-labelledby={staffId}
                            name={staffId}
                            sx={{
                              flex: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: { xs: 'space-between', sm: 'center' },
                            }}
                          >
                            {attendanceOptions.map((eachAttendanceOption) => (
                              <FormControlLabel
                                checked={
                                  trackedStaff[staffId]?.attendanceStatus ===
                                  eachAttendanceOption.value
                                }
                                value={eachAttendanceOption.value}
                                onChange={() => {
                                  handleStaffTrackChange(
                                    eachAttendanceOption.value,
                                    staffId
                                  );
                                }}
                                key={eachAttendanceOption.value}
                                control={
                                  <Radio
                                    size={isMobile ? 'medium' : 'small'}
                                    sx={{
                                      minHeight: { xs: '44px', sm: 'auto' },
                                      minWidth: { xs: '44px', sm: 'auto' },
                                    }}
                                    color={
                                      eachAttendanceOption.color as
                                      | 'primary'
                                      | 'secondary'
                                      | 'error'
                                      | 'info'
                                      | 'success'
                                      | 'warning'
                                      | 'default'
                                    }
                                  />
                                }
                                label={isMobile ? eachAttendanceOption.label : ''}
                                sx={{
                                  flex: { xs: 1, sm: 1 },
                                  textAlign: 'center',
                                  m: 0,
                                  '& .MuiFormControlLabel-label': {
                                    fontSize: { xs: '0.75rem', sm: 'inherit' },
                                  },
                                }}
                              />
                            ))}
                          </RadioGroup>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'flex-start', sm: 'center' },
                            mt: { xs: 0.5, sm: 0 },
                          }}
                        >
                          <IconButton
                            color="primary"
                            aria-label={translateText(
                              'staff.attendance.addComments'
                            )}
                            size={isMobile ? 'medium' : 'small'}
                            onClick={() => handleCommentToggle(staffId)}
                            sx={{
                              minHeight: { xs: '44px', sm: 'auto' },
                              minWidth: { xs: '44px', sm: 'auto' },
                            }}
                            data-testid={`comment-button-${staffId}`}
                          >
                            <Comment />
                          </IconButton>
                        </Grid>
                      </Grid>
                      {openCommentFields[staffId] && (
                        <Grid item xs={12} sx={{ mt: { xs: 1, sm: 1 } }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label={translateText('staff.attendance.comment')}
                            value={
                              comments[staffId] ||
                              trackedStaff[staffId]?.comment ||
                              ''
                            }
                            onChange={(e) =>
                              handleCommentChange(staffId, e.target.value)
                            }
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '16px', sm: '14px' },
                              },
                            }}
                            data-testid={`comment-field-${staffId}`}
                          />
                        </Grid>
                      )}
                    </FormGroup>
                  </Grid>
                );
              }
            )}
          </Grid>
        </Grid>
      </Grid>
    </StructuredModal>
  );
};

export default StaffAttendanceTracker;
